<template>
  <div
    v-appendTo="appendTo"
    :style="style"
    class="rotate-module"
  >
    <!--
      'id' - Used as prop to editor menus so menus are positioned away from rotation handle.
      'data-vue-grid-item-rotation-handle-id' - This makes this element a rotation handle for the
        specified vue-grid-item id.
      'editor-floating-menu-module-id' - Allows floating handle to be treated the same as an editor
        floating menu i.e When you click it, the module stays active.
    -->
    <div
      :id="`rotate-module-button-${moduleData.id}`"
      :data-vue-grid-item-rotation-handle-id="moduleData.id"
      :editor-floating-menu-module-id="moduleData.id"
      :class="{'rotate-module__handle--rotating': isRotating }"
      class="rotate-module__handle"
    >
      <NebulaIcon
        symbol-id="rotate"
        size="s"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import {
  cloneDeep, get, isNumber, isPlainObject, throttle,
} from 'lodash-es';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { bus } from '@/lib/eventBus';
import appendTo from '@/directives/appendTo';
import moduleRotation from '@/mixins/moduleRotation';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

const nebulaSpace2x = parseInt(nebulaTokens.space['2x'], 10);

export default {
  components: {
    NebulaIcon,
  },
  directives: {
    appendTo,
  },
  mixins: [moduleRotation],
  props: {
    appendTo: {
      type: HTMLElement,
      required: true,
    },
    moduleElement: {
      type: HTMLElement,
      default: null,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    canvasName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      style: null,
      rotationChange: null,
    };
  },
  created() {
    this.updateStyleThrottled = throttle(this.updateStyle.bind(this), 25);
  },
  mounted() {
    this.$nextTick(() => {
      this.attachRotationHandlers();
      this.updateStyle();

      bus.on('update-module-options', this.updateStyleThrottled);
      window.addEventListener('resize', this.updateStyleThrottled);
      bus.on('moved', this.onModuleUpdated);
      bus.on('resized', this.onModuleUpdated);
      bus.on('window:scroll', this.updateStyleThrottled);
      bus.on('module:move', this.onModuleUpdated);
      bus.on('module:resize', this.onModuleUpdated);
      bus.on('module:updated', this.onModuleUpdated);
      bus.on('module:auto-resizing-y', this.onModuleUpdated);
      bus.on('editor:zoom-changed', this.updateStyleThrottled);
      bus.on('editor:resized', this.updateStyleThrottled);
      if (this.editorStageEl) this.editorStageEl.addEventListener('scroll', this.updateStyleThrottled);
    });
  },
  beforeUnmount() {
    bus.off('update-module-options', this.updateStyleThrottled);
    window.removeEventListener('resize', this.updateStyleThrottled);
    bus.off('moved', this.onModuleUpdated);
    bus.off('resized', this.onModuleUpdated);
    bus.off('window:scroll', this.updateStyleThrottled);
    bus.off('module:move', this.onModuleUpdated);
    bus.off('module:resize', this.onModuleUpdated);
    bus.off('module:updated', this.onModuleUpdated);
    bus.off('module:auto-resizing-y', this.onModuleUpdated);
    bus.off('editor:zoom-changed', this.updateStyleThrottled);
    bus.off('editor:resized', this.updateStyleThrottled);
    if (this.editorStageEl) this.editorStageEl.removeEventListener('scroll', this.updateStyleThrottled);
    this.removeRotationHandlers();
  },
  computed: {
    isRotating() {
      return isNumber(this.rotationChange);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULE,
    ]),
    updateStyle() {
      /*
        Rotate this component around the center point of the grid item
        as the grid item is rotated. This is needed since this rotation
        handle component is not inside the vue-grid-item, but instead is
        floating above so it can be visible over other modules when selected.
      */
      const moduleRect = this.getModuleRectNotScaled();
      if (!moduleRect) return;
      const buttonPadding = nebulaSpace2x;
      const buttonRadius = nebulaSpace2x;
      const buttonPaddingNotScaled = buttonPadding / this.scale;
      const buttonRadiusNotScaled = buttonRadius / this.scale;
      // Get the current radius of the rotation handle to center of the grid item.
      const rotationRadius = moduleRect.width / 2
        + buttonPaddingNotScaled + buttonRadiusNotScaled;
      // Get the current rotation angle.
      const rotationDegrees = this.isRotating
        ? this.rotationChange
        : (get(this, 'moduleData.layout.rotate_degree') || 0);
      const rotationRadians = rotationDegrees * (Math.PI / 180);
      /*
        Calculate the length of the opposite and adjacent sides of the right
        triangle this angle forms.
      */
      const buttonYOffset = Math.sin(rotationRadians) * rotationRadius;
      const buttonXOffset = Math.cos(rotationRadians) * rotationRadius;
      /*
        Use the offsets above to calculate the rotated position of the handle.
      */
      const buttonY = moduleRect.centerY + buttonYOffset;
      const buttonX = moduleRect.centerX + buttonXOffset;
      this.style = {
        left: `${buttonX}px`,
        top: `${buttonY}px`,
        transform: `scale(${1 / this.scale})`,
      };
    },
    onModuleUpdated(payload) {
      const moduleId = isPlainObject(payload)
        ? payload.updatedModule.id
        : payload;
      if (moduleId === this.moduleData.id) {
        /*
          Wait two cycles before updating style to ensure module
          changes have been rendered.
        */
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.updateStyleThrottled();
          });
        });
      }
    },
    onRotationStart(args) {
      if (args.id !== this.moduleData.id) return;
      this.rotationChange = args.rotationDegrees;
      this.updateStyle();
    },
    onRotationChange(args) {
      if (args.id !== this.moduleData.id) return;
      this.rotationChange = args.rotationDegrees;
      this.updateStyle();
    },
    onRotationEnd(args) {
      if (args.id !== this.moduleData.id) return;
      this.rotationChange = null;
      if (!isNumber(args.rotationDegrees)) {
        return;
      }
      const modData = cloneDeep(this.moduleData);
      modData.layout.rotate_degree = args.rotationDegrees;
      this[types.UPDATE_MODULE]({
        canvasName: this.canvasName,
        payload: modData,
      });
      this.$nextTick(this.updateStyle);
    },
  },
};
</script>

<style lang="stylus">
.rotate-module {
  position: fixed;
  z-index: 799; // Under the mini menu

  &__handle {
    align-items: center;
    background-color: $nebula-color-platform-neutral-500;
    border-radius: 50%;
    cursor: grab;
    display: flex;
    height: $nebula-space-4x;
    justify-content: center;
    left: "calc(50% - %s)" % $nebula-space-2x;
    position: absolute;
    top: "calc(50% - %s)" % $nebula-space-2x;
    transition: $nebula-transition-default;
    transition-property: background-color;
    width: $nebula-space-4x;

    &--rotating {
      cursor: grabbing;
    }

    svg.nebula-icon {
      fill: $nebula-color-white;
    }

    &:hover {
      background-color: $nebula-color-platform-neutral-700;
    }
  }
}
</style>
